import { useRef, useState } from 'react'
import { Loader2, Upload } from 'lucide-react'
import { meetingsAPI } from '../../services/api'
import { useToast } from '../common/Toast'
import { MEETING_DOCUMENT_ACCEPT } from '../../constants/meetingDocuments'

function uploadScopeFromListScope(listScope) {
  if (listScope === 'series') return 'series'
  return 'meeting'
}

/**
 * Single rithmo-blue upload control for meeting-builder agenda rows (no helper copy or lists).
 */
export default function AgendaItemUploadButton({
  meetingId,
  listScope = 'meeting',
  preparing = false,
  onEnsureMeeting,
  onUploaded,
  disabled = false,
  disabledTitle,
}) {
  const fileInputRef = useRef(null)
  const [uploading, setUploading] = useState(false)
  const toast = useToast()

  const busy = preparing || uploading
  const blocked = disabled || busy

  const openPicker = async () => {
    if (blocked) return
    let mid = meetingId
    if (!mid && onEnsureMeeting) {
      mid = await onEnsureMeeting()
    }
    if (!mid) return
    fileInputRef.current?.click()
  }

  const onFileChange = async (event) => {
    const file = event.target.files?.[0]
    event.target.value = ''
    if (!file) return

    let mid = meetingId
    if (!mid && onEnsureMeeting) {
      mid = await onEnsureMeeting()
    }
    if (!mid) return

    setUploading(true)
    try {
      await meetingsAPI.uploadAttachment(mid, file, {
        scope: uploadScopeFromListScope(listScope),
        shareWithAllParticipants: true,
      })
      toast.success('Document uploaded', file.name)
      onUploaded?.()
    } catch (err) {
      const message =
        typeof err?.message === 'string' && err.message.trim() ? err.message : 'Request failed.'
      toast.error('Upload failed', message)
    } finally {
      setUploading(false)
    }
  }

  return (
    <>
      <input
        ref={fileInputRef}
        type="file"
        accept={MEETING_DOCUMENT_ACCEPT}
        className="sr-only"
        aria-hidden
        onChange={onFileChange}
      />
      <button
        type="button"
        disabled={blocked}
        title={disabled ? disabledTitle : undefined}
        onClick={() => void openPicker()}
        className="btn-primary inline-flex items-center gap-2 rounded-xl px-4 py-2 text-sm font-semibold disabled:cursor-not-allowed disabled:opacity-50"
      >
        {busy ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" aria-hidden />}
        Upload document
      </button>
    </>
  )
}
